import type { ICaseResponse } from '@escloud/types';

import type { CaseMetadata, IWorkflowService } from './workflows/base-workflow-service';

// ============================================================================
// Types
// ============================================================================

/**
 * Minimal interface for fetching case data.
 * Keeps the visualization layer decoupled from API auth concerns.
 */
export interface ICaseProvider {
    getById(id: string): Promise<ICaseResponse>;
}

export interface ViewerSessionResult {
    workflowId: string;
    caseMetadata: CaseMetadata;
    workflowService: IWorkflowService;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Extract the CaseMetadata subset from a full ICaseResponse.
 */
function extractMetadata(caseData: ICaseResponse): CaseMetadata {
    return {
        id: caseData.id,
        name: caseData.name,
        firstname: caseData.firstname,
        lastname: caseData.lastname,
        dob: caseData.dob,
        gender: caseData.gender,
        createdAt: caseData.createdAt,
    };
}

// ============================================================================
// Orchestrator
// ============================================================================

/**
 * Plain orchestrator class that encapsulates the imperative startup sequence:
 *   1. Fetch case data
 *   2. Validate workflowId
 *   3. Extract CaseMetadata
 *   4. Resolve the workflow service and set metadata
 *
 * This is intentionally NOT a PubSubService — it runs once per viewer mount
 * and does not maintain long-lived state.
 */
export class ViewerSessionOrchestrator {
    constructor(
        private casesApi: ICaseProvider,
        private getWorkflowService: (workflowId: string) => IWorkflowService
    ) {}

    async initialize(caseId: string, signal?: AbortSignal): Promise<ViewerSessionResult> {
        const caseData = await this.casesApi.getById(caseId);

        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

        if (!caseData.workflowId) {
            throw new Error('This case has no workflow assigned. Please contact support.');
        }

        const caseMetadata = extractMetadata(caseData);
        const workflowService = this.getWorkflowService(caseData.workflowId);
        workflowService.setCaseMetadata(caseMetadata);

        return {
            workflowId: caseData.workflowId,
            caseMetadata,
            workflowService,
        };
    }
}
